import type { Event, KeyCopy, KeyProofOp, SenderProof } from "@timecapsule/crypto";
import { msg } from "./i18n";

/** A fresh server challenge signed with a secret key (kind 22242): proves this browser holds the key. */
export type KeyProof = { challenge: string; proof: Event };
/** The server copy of the active identity, with the server's PIN attempt counter. */
export type ServerCopy = { copy: KeyCopy; attempts_left: number; locked: boolean; updated_at: number };

/** Profile of an identity (PLAN.md 4.10); the source of the sender snapshot in new capsules. */
export type Profile = { display_name: string; name: string | null; about: string | null; profile_at: number | null };
/** `display_name` is the login's (Google) name; `profile` belongs to the active identity. */
export type Me = {
  id: number;
  email: string;
  display_name: string;
  pubkey: string | null;
  /** PLAN.md 4.22: one owner, admins the owner names, members. */
  role: "owner" | "admin" | "member";
  profile: Profile | null;
  /** Attachments per capsule: total bytes before encryption, and number of files (server config). */
  limits: { attachment_bytes: number; files: number; messages: number; open_invites?: number | null };
  /** `window_seconds` null: a service account's budget is a total (PLAN.md 4.16 K5). */
  usage: { messages: number; window_seconds: number | null };
  /** "service": uses API tokens; only the member and the admin see it. */
  kind: "member" | "service";
  /** Plan, stamps and storage (PLAN.md 4.24); null for a service account. */
  billing: BillingSummary | null;
  /** The member's own settings (PLAN.md 4.16 K10); `mail_lang`: the language of their mails (PLAN.md 4.28). */
  settings: { hide_senders: boolean; mail_lang?: string | null };
  /** The account ever had a key: any further one needs the password or a passkey (PLAN.md 4.18 step 5). */
  had_key: boolean;
  /** Every key the account ever had, current and earlier (PLAN.md 4.16 K4, 4.20). */
  keys: string[];
};
/** PLAN.md 4.24: plan, stamps and storage of a retail member. No payment is collected in this release. */
export type BillingSummary = {
  plan: { id: PlanId; monthly_units: number; attachment_bytes: number };
  stamps: {
    remaining: number;
    period: { key: string; granted: number; remaining: number; consumed: number; ends_at: number };
    held_pending: number;
    expiring_soon: { units: number; expires_at: number }[];
    expiring_soon_truncated: boolean;
  };
  storage: { used_bytes: number; quota_bytes: number; over_quota: boolean };
  /** `renewal`: the running subscription at the provider (PLAN.md 4.27 R6); `auto_renew` null when not reported yet. */
  subscription: { activated_through: number | null; renewal: { auto_renew: boolean | null; cancel_at: number | null } | null };
};
export type PlanId = "free" | "legacy" | "start";
export type LaunchState = { switch: boolean; count: number; cap: number; open: boolean };
/** One of our own levels of rights (PLAN.md 4.26): Free, Legacy, Start. Not a price. */
export type CataloguePlan = { id: PlanId; rank: number; monthly_units: number; base_storage_bytes: number; attachment_bytes: number };
/** A price the payment provider sells, with what its mapping grants (PLAN.md 4.26 P1, P2). */
export type Offer = {
  price_id: string;
  name: string;
  description: string | null;
  /** Integer minor units (cents). */
  amount: number;
  currency: string;
  interval: "month" | "year" | null;
  interval_count: number;
  kind: "subscription" | "pack";
  plan_id: PlanId | null;
  stamps: number;
  stamp_months: number;
  extra_storage_bytes: number;
  attachment_bytes: number;
  attachment_months: number;
};
export type Catalogue = { provider: "fake" | "paddle" | "dodo"; environment: string; /** Members may open a checkout (the server decides: a live provider, or local and CI). */ member_checkout: boolean; /** Paddle's public client-side token for its checkout on our page. */ client_token: string | null; plans: CataloguePlan[]; offers: Offer[]; launch: LaunchState };
/** A purchase at the provider: started when the member opened its checkout, completed by the provider's event. */
export type OrderView = { id: string; provider: string; price_id: string; status: "open" | "completed" | "expired" | "unknown"; created_at: number; completed_at: number | null };
export type EntitlementView = {
  id: number;
  plan_id: PlanId | null;
  source: "launch" | "manual" | "subscription" | "pack_storage" | "pack_attachment";
  starts_at: number;
  ends_at: number | null;
  monthly_units: number;
  base_storage_bytes: number;
  extra_storage_bytes: number;
  attachment_bytes: number;
  order_id: string | null;
  /** Admin view only. */
  reason?: string | null;
  ref?: string;
};
/** A stamp use: a send, a reservation for a waiting capsule, or the refund of one. */
export type UseRow = { id: string; kind: "send" | "reserve" | "refund"; units: number; created_at: number; refund_of: string | null; messages: number; pending_id: string | null; pending_status: string | null; pending_expires_at: number | null };
export type GrantRow = { id: number; reason: "period" | "pack" | "manual" | "refund"; units: number; remaining: number; starts_at: number; expires_at: number; created_at: number; order_id: string | null; note?: string | null };
export type HistoryKind = "uses" | "grants" | "orders";
export type BillingView = BillingSummary & {
  launch: LaunchState;
  entitlements: EntitlementView[];
  /** The member's subscription at the provider, if one runs. */
  provider_subscription: { provider: string; subscription_id: string; price_id: string; created_at: number } | null;
  /** Admin view only: the member's customer ids at the providers. */
  provider_customers?: { provider: string; customer_id: string }[];
  /** The member bought something at a provider: the portal can be opened. */
  has_portal: boolean;
};
/** What one provider price grants (PLAN.md 4.26 P2); edited by the owner on Admin -> Payments. */
export type PriceMapping = {
  provider: "fake" | "paddle" | "dodo";
  price_id: string;
  kind: "subscription" | "pack";
  plan_id: "legacy" | "start" | null;
  stamps: number;
  stamp_months: number;
  extra_storage_bytes: number;
  attachment_bytes: number;
  attachment_months: number;
  active: 0 | 1;
};
/** The payment provider for the admin: keys set or not (never their values), the webhook address, every price it sells. */
export type AdminPrices = {
  provider: "fake" | "paddle" | "dodo";
  environment: "sandbox" | "live";
  secrets: { api_key: boolean; webhook_secret: boolean; client_token: boolean };
  webhook_url: string;
  catalogue_error: string | null;
  offers: { price_id: string; name: string; description: string | null; amount: number; currency: string; interval: "month" | "year" | null; interval_count: number; custom_data: Record<string, unknown> | null; mapping: PriceMapping | null }[];
  other_mappings: PriceMapping[];
};
/** A signed provider event that granted nothing yet. */
export type WaitingEvent = { provider: string; event_id: string; type: string; occurred_at: number; received_at: number; member_id: number | null; member_email: string | null; status: "unmapped" | "unmatched" | "flagged" | "invalid"; price_id: string | null; detail: string | null };
export type AdminOrder = OrderView & { member_id: number; member_email: string; provider_transaction_id: string | null; provider_subscription_id: string | null };
/** What a send returns about the stamps it used (PLAN.md 4.24); null for a service account. */
export type SubmitBilling = { use_id: string; units: number; remaining: number | null } | null;

/** Directory entry. `key_since`: when the active key became active (unix seconds). */
/** The owner and admins run the Admin page (PLAN.md 4.22). */
export const isAdminRole = (role: string) => role === "owner" || role === "admin";
export type Member = { id: number; display_name: string; name: string | null; pubkey: string | null; keys?: string[]; key_since: number | null; role: string };
/** Admin page view of a member: the directory entry plus email and state. */
export type UserStatus = 'predefined'|'active'|'suspended'|'deactivated';
export type PilotState = { max_members:number;member_invite_limit:number;occupied:number;remaining:number;counts:Record<UserStatus,number>;services:number };
export type UserInvitation = {code:string;created_by:number;used_by:number|null;created_at:number;expires_at:number;revoked_at:number|null;mailed:number;mail_error:string|null;inviter_name:string};
export type PredefinePreview = {add:string[];existing:{email:string;status:UserStatus}[];invalid:string[];duplicates:string[];pilot:PilotState};
export type AdminMember = Member & { status:UserStatus;status_reason:string|null;registered_at:number|null;status_version:number;origin:string;referral_code:string|null;inviter_name:string|null; email: string; created_at: number; deactivated_at: number | null; server_copy: number; kind: "member" | "service" };
/** An API token of a service account (PLAN.md 4.16 K5); the token itself is shown only when made. */
export type ApiToken = { id: number; name: string; prefix: string; scopes: string; created_at: number; last_used_at: number | null; revoked_at: number | null };
export type Invite = {
  revoked_at: number | null;
  code: string;
  email: string | null;
  created_by: number;
  used_by: number | null;
  /** A login started with this invite; it becomes "used" once the member is created. */
  claimed: boolean;
  /** The current name of the member who used it (PLAN.md 4.20). */
  used_by_name: string | null;
  expires_at: number;
  created_at: number;
};
/** One page of a list (PLAN.md 4.20); `next_cursor` is null on the last page. */
export type Page<T> = { items: T[]; next_cursor: string | null };
const page = <K extends string, T>(key: K) => (r: { [k in K]: T[] } & { next_cursor: string | null }): Page<T> => ({ items: r[key], next_cursor: r.next_cursor });
const withCursor = (path: string, cursor?: string, extra: Record<string, string> = {}) => {
  const query = new URLSearchParams({ ...extra, ...(cursor ? { cursor } : {}) }).toString();
  return query ? `${path}?${query}` : path;
};
export type MessageRow = {
  id: string;
  kind: 1041 | 1059;
  author_pubkey: string | null;
  recipient_pubkey: string | null;
  round: number | null;
  /** Unix seconds; for a private wrap from its plaintext "unlock" tag. */
  unlock_at: number;
  created_at: number;
  /** Server clock; lists are ordered and paged by (received_at, id). */
  received_at: number;
  /** For the author's "keep a copy" wrap: id of the wrap it copies. */
  copy_of: string | null;
  /** Group send (PLAN.md 4.14): id of its first wrap, on every wrap and the copy. */
  batch_of: string | null;
  /** sha256 of the encrypted attachment blobs the capsule uses (known to the server from the submission). */
  blobs: string[];
  /** Inbox only (PLAN.md 4.16 K9): the reader blocked the member who sent it. */
  blocked?: boolean;
  /** Private wraps to one of my keys (PLAN.md 4.18): the sending account's key, encrypted to the wrap's key. */
  sender_proof?: SenderProof;
  /** K2: signed by a key its owner later revoked as stolen, and received after "stolen since". */
  suspect?: boolean;
  /** Listings (PLAN.md 4.20): this account archived the capsule. */
  archived?: boolean;
  /** Listings: this account opened the capsule on some device, or sent it; unlocked and not read is "new". */
  read?: boolean;
  /** Sent (PLAN.md 4.20): the key of every wrap of the capsule (each member of a group send). */
  recipients?: string[];
  /** Sent (PLAN.md 4.20): the author's own copy, listed with its wrap instead of as a row. */
  copy?: MessageRow | null;
  /** Reply envelope (PLAN.md 4.21): the wrap asks its recipient for a reply, opening at `unlock` (unix seconds) or when they choose (null). */
  sase: { unlock: number | null; expired: boolean } | null;
  /** The wrap this capsule answers (PLAN.md 4.21), or null. */
  reply_to: string | null;
  /** Replies only: the request sender's stamp paid for it; otherwise the replier did. */
  stamped?: boolean;
  size_bytes: number;
  event: Event;
};

/** A reply under its request (PLAN.md 4.21): numbered in order of receipt, with the replier's own copy. */
export type ReplyRow = MessageRow & { reply_index: number; copy: MessageRow | null };
/** A request I sent with a reply envelope (`/api/correspondence/sent`): as a Sent row, plus each recipient's replies. */
export type SentEnvelopeRow = MessageRow & {
  recipients: string[];
  copy: MessageRow | null;
  paid_units: number;
  replied: number;
  total: number;
  by_recipient: { pubkey: string; member_id: number | null; replies: ReplyRow[]; more_replies: boolean }[];
};
/** A request to me with a reply envelope (`/api/correspondence/received`), with my own replies to it. */
export type ReceivedEnvelopeRow = MessageRow & { my_replies: ReplyRow[]; more_replies: boolean };

/** One page of a list (PLAN.md 4.20); `next_cursor` is null on the last page. */
export type MessagePage<T extends MessageRow = MessageRow> = { messages: T[]; next_cursor: string | null };

export type Submitted = { id: string; ids: string[]; kind: number; unlock_at: number | null; stored: boolean; inserted?: number; billing?: SubmitBilling };
/** The message lists; `envelopes_*` are the two Correspondence lists (PLAN.md 4.21). */
export type Listing = "feed" | "inbox" | "sent" | "watching" | "envelopes_sent" | "envelopes_received";
/** Rows of these listings are what I sent: the recipient is named, the sender is me. */
export const sentListing = (listing: Listing) => listing === "sent" || listing === "envelopes_sent";

/** One sign-in method of the member (PLAN.md 4.14). `label`: the address of a password login. */
export type SignIn = { id: number; provider: "google" | "password" | "passkey"; label: string | null; created_at: number; last_used_at: number | null };
/** What a mail link is for, before it is used. */
export type MailLinkInfo = { purpose: "register" | "add" | "reset" | "recover"; email: string };
/** `dev_link`: local development only, where mails go to the server log. */
export type MailSent = { ok: true; dev_link?: string };
/** A signed passkey assertion as POST /api/auth/passkey/verify takes it (base64url fields). */
/** A member I follow (PLAN.md 4.14) with their directory entry; `pubkey` is their active key, null before they made one. */
export type Follow = Omit<Member, "id"> & { member_id: number; source: "chosen" | "invited" | "inviter"; created_at: number; /** 1: they block me (PLAN.md 4.16 K9). */ blocks_me: number };
/** A key of the account (PLAN.md 4.16 K1): active while retired_at is null; revoked keys never return. */
export type KeyRecord = { pubkey: string; registered_at: number; activated_at: number; retired_at: number | null; revoked_at: number | null; compromised_since: number | null; last_used_at: number | null };
/** A block, a hide or a follower (PLAN.md 4.16 K9): which member (with their directory entry, 4.20) and since when. */
export type PersonEntry = Omit<Member, "id"> & { member_id: number; created_at: number };
/** The address book as the server keeps it: a NIP-44 payload only my key opens; version 0 = none yet. */
export type StoredAddressBook = { version: number; blob: string | null; updated_at: number | null };
/** `resent`: the address already had my open invite; it was sent again with a new expiry. */
export type InviteCreated = { invite: Invite; link: string; mailed: boolean; resent?: boolean; mail_error?: string };

/** A capsule waiting for my address (PLAN.md 4.14, "sender approves"). */
export type PendingIn = {
  id: string;
  status: "waiting" | "requested" | "declined" | "expired" | "delivered";
  subject: string | null;
  unlock_at: number | null;
  /** The copy carries a reply envelope (PLAN.md 4.21). */
  sase: { unlock: number | null } | null;
  expires_at: number;
  sender_name: string;
  requested_by_me: boolean;
};
/** A capsule I sent to an address, and who asked for it. */
export type PendingOut = Omit<PendingIn, "sender_name" | "requested_by_me"> & {
  email: string;
  copy_id: string;
  requester_name: string | null;
  requested_pubkey: string | null;
  delivered_id: string | null;
};

export type PasskeyAssertion = { credential_id: string; client_data_json: string; authenticator_data: string; signature: string };

/** An API error: HTTP status, the stable `code` the app translates (4b step 3b), English detail as message. */
export class ApiError extends Error {
  constructor(
    readonly status: number,
    message: string,
    readonly code: string | null = null,
  ) {
    super(message);
  }
}

async function call<T>(method: string, path: string, body?: unknown, signal?: AbortSignal): Promise<T> {
  const res = await fetch(path, {
    method,
    signal,
    credentials: "same-origin",
    headers: body === undefined ? { accept: "application/json" } : { accept: "application/json", "content-type": "application/json" },
    body: body === undefined ? undefined : JSON.stringify(body),
  });
  const text = await res.text();
  let json: unknown = null;
  try {
    json = text ? JSON.parse(text) : null;
  } catch {
    // Non-JSON error bodies (e.g. Hono's plain-text HTTPException) are reported as text.
  }
  if (!res.ok) {
    const body = json as { error?: string; code?: string } | null;
    if(body?.code==='member_suspended') window.dispatchEvent(new Event('vrelis:suspended'));
    throw new ApiError(res.status, body?.error ?? (text || res.statusText), body?.code ?? null);
  }
  return json as T;
}

export const NOTIFICATION_KINDS = ["new_private", "new_public", "upcoming", "reply_due", "unlocked"] as const;
export type NotificationKind = (typeof NOTIFICATION_KINDS)[number];
/** `lead_hours` only for "upcoming" and "reply_due", `attach` only for "unlocked". */
export type NotificationPrefs = { enabled: boolean; lead_hours?: number; attach?: boolean };
export type NotificationsAnswer = { notifications: Record<NotificationKind, NotificationPrefs>; lead_hours: number[]; lang: string | null };

export const api = {
  /** Returns null when not logged in. */
  async me(): Promise<Me | null> {
    try {
      return await call<Me>("GET", "/api/me");
    } catch (err) {
      if (err instanceof ApiError && err.status === 401) return null;
      throw err;
    }
  },
  logout: () => call<{ ok: true }>("POST", "/api/auth/logout"),
  /** `event`: kind 0 signed by the active key. */
  saveProfile: (event: Event) => call<{ profile: Profile }>("PUT", "/api/profile", { event }),
  logoutEverywhere: () => call<{ ok: true }>("POST", "/api/auth/logout-everywhere"),
  devLogin: (body: { email: string; name?: string; secret: string; invite?: string }) => call("POST", "/api/auth/dev", body),

  // Password and passkey sign-in (PLAN.md 4.14). `stretched` is stretchPassword(), never the password.
  passwordRegister: (body: { email: string; stretched: string; name?: string; invite?: string; lang: string; recover?: boolean }) =>
    call<MailSent>("POST", "/api/auth/password/register", body),
  passwordLogin: (body: { email: string; stretched: string }) => call<{ ok: true }>("POST", "/api/auth/password/login", body),
  recoveryChallenge:()=>call<{challenge:string}>('GET','/api/recovery/challenge'),
  recoverAccess:(proof:KeyProof)=>call<{ok:true;member_id:number}>('POST','/api/recovery/complete',proof),
  passwordReset: (body: { email: string; lang: string }) => call<MailSent>("POST", "/api/auth/password/reset", body),
  mailLinkInfo: (token: string) => call<MailLinkInfo>("POST", "/api/auth/password/token", { token }),
  /** Uses a mail link once and signs in; a reset brings the new password. */
  useMailLink: (token: string, stretched?: string) =>
    call<{ ok: true; purpose: MailLinkInfo["purpose"] }>("POST", "/api/auth/password/verify", { token, ...(stretched ? { stretched } : {}) }),
  passkeyOptions: () => call<{ challenge: string; token: string; rp_id: string }>("POST", "/api/auth/passkey/options"),
  passkeyLogin: (token: string, assertion: PasskeyAssertion) => call<{ ok: true }>("POST", "/api/auth/passkey/verify", { token, ...assertion }),
  /** "Confirm it is you" (PLAN.md 4.16 K3): a sign-in that must prove the signed-in member. */
  reauthPassword: (email: string, stretched: string) => call<{ ok: true }>("POST", "/api/auth/password/login", { email, stretched, reauth: true }).then(() => undefined),
  reauthPasskey: (token: string, assertion: PasskeyAssertion) => call<{ ok: true }>("POST", "/api/auth/passkey/verify", { token, ...assertion, reauth: true }).then(() => undefined),
  signIns: () => call<{ logins: SignIn[] }>("GET", "/api/logins").then((r) => r.logins),
  /** Sends a link to the member's address; the password exists after it is opened. */
  addPassword: (body: { stretched: string; lang: string }) => call<MailSent>("POST", "/api/logins/password", body),
  /** Lets a passkey sign in; the proof is by the active key over [credential id, alg, public key]. */
  enrolPasskey: (body: { credential_id: string; alg: number; public_key: string }, p: KeyProof) => call<{ ok: true }>("POST", "/api/logins/passkey", { ...body, ...p }),
  removeSignIn: (id: number) => call<{ ok: true }>("DELETE", `/api/logins/${id}`),
  /** Member search by name (PLAN.md 4.20 step 5): at least 2 letters, paged. */
  searchMembers: (q: string, cursor?: string, limit = 20) =>
    call<{ members: Member[]; next_cursor: string | null }>("GET", `/api/members?${new URLSearchParams({ q, limit: String(limit), ...(cursor ? { cursor } : {}) })}`),
  /** Directory entries of these members and of the owners of these keys (at most 100 together). */
  lookupMembers: (ids: number[], keys: string[]) => call<{ members: Member[] }>("POST", "/api/members/lookup", { ids, keys }).then((r) => r.members),

  /** A challenge for one key proof: the operation and, where it has content, the content's hash (PLAN.md 4.13 R2). */
  keyChallenge: (op: KeyProofOp, bind: string | null) =>
    call<{ challenge: string }>("GET", `/api/keys/challenge?op=${op}${bind ? `&bind=${bind}` : ""}`).then((r) => r.challenge),
  /** `current`: proof by the active key, required when the member already has one (PLAN.md 4.11 H1). */
  registerKey: (p: KeyProof, current?: KeyProof) =>
    call<{ pubkey: string; previous: string | null }>("POST", "/api/keys/register", { ...p, ...(current ? { current } : {}), lang: document.documentElement.lang || "en" }),
  /** Every key of the account with its state (PLAN.md 4.16 K1). */
  keys: () => call<{ keys: KeyRecord[] }>("GET", "/api/keys").then((r) => r.keys),
  /** Revokes the active key (K2): a proof by that key over [pubkey, compromised_since or ""]. */
  revokeKey: (p: KeyProof, compromisedSince: number | null) =>
    call<{ ok: true }>("POST", "/api/keys/revoke", { ...p, compromised_since: compromisedSince, lang: document.documentElement.lang || "en" }),
  /** The keyring (K4): encrypted to the current key in the browser, opaque to the server. */
  keyring: () => call<{ version: number; blob: string | null }>("GET", "/api/keys/keyring"),
  saveKeyring: (version: number, blob: string) => call<{ version: number }>("PUT", "/api/keys/keyring", { version, blob }),
  /** Google re-link: this new, empty login takes over the member who owns the proven identity. */
  relink: (p: KeyProof) => call<{ member_id: number; display_name: string; pubkey: string }>("POST", "/api/keys/relink", p),
  /** `oprfKeyId`: the key id the OPRF evaluation for this copy answered with. */
  saveServerCopy: (copy: KeyCopy, oprfKeyId: string, p: KeyProof) => call<{ ok: true }>("PUT", "/api/keys/backup", { copy, oprf_key_id: oprfKeyId, ...p }),
  /** Returns null when the active identity has no server copy. */
  async loadServerCopy(): Promise<ServerCopy | null> {
    try {
      return await call<ServerCopy>("GET", "/api/keys/backup");
    } catch (err) {
      if (err instanceof ApiError && err.status === 404) return null;
      throw err;
    }
  },
  /** OPRF on a blinded PIN. With a key proof it is not counted; without, it is one attempt (423 when locked). */
  oprf: (request: string, p?: KeyProof) =>
    call<{ response: string; counted: boolean; attempts_left?: number; key_id: string }>("POST", "/api/keys/oprf", { request, ...p }),
  keyUnlocked: (p: KeyProof) => call<{ ok: true }>("POST", "/api/keys/unlocked", p),

  /** Bound to `email` and mailed to it, or (admins) `anyone: true` for a link anyone can use. */
  createInvite: (body: { email: string; expires_in_days: number; lang?: string; service?: boolean;code?:string } | { anyone: true; expires_in_days: number }) =>
    call<InviteCreated>("POST", "/api/invites", body),
  revokeInvite: (code: string) => call<{ ok: true }>("DELETE", `/api/invites/${encodeURIComponent(code)}`),
  tokens: () => call<{ tokens: ApiToken[] }>("GET", "/api/tokens").then((r) => r.tokens),
  createToken: (name: string, scopes: string[]) =>
    call<{ id: number; token: string; name: string; prefix: string; scopes: string[] }>("POST", "/api/tokens", { name, scopes, lang: document.documentElement.lang || "en" }),
  revokeToken: (id: number) => call<{ ok: true }>("DELETE", `/api/tokens/${id}`),
  adminTokens: (memberId: number) => call<{ tokens: ApiToken[] }>("GET", `/api/admin/members/${memberId}/tokens`).then((r) => r.tokens),
  adminRevokeToken: (id: number) => call<{ ok: true }>("DELETE", `/api/admin/tokens/${id}`),
  connectInfo: (code: string) => call<{ inviter: { id: number; display_name: string } }>("GET", `/api/invites/${encodeURIComponent(code)}/connect`),
  acceptConnect: (code: string) => call<{ ok: true }>("POST", `/api/invites/${encodeURIComponent(code)}/accept`),
  declineConnect: (code: string) => call<{ ok: true }>("POST", `/api/invites/${encodeURIComponent(code)}/decline`),
  /** Admins: every invite; `mine` (and every member): the invites I made. */
  invites: (mine: boolean, cursor?: string) => call<{ invites: Invite[]; next_cursor: string | null }>("GET", withCursor("/api/invites", cursor, { limit: "20", ...(mine ? { mine: "1" } : {}) })).then(page<"invites", Invite>("invites")),
  config: () => call<{ registration_open: boolean }>("GET", "/api/config"),
  pilot:()=>call<PilotState>('GET','/api/admin/pilot'),
  unassignedInvites:(cursor?:string)=>call<{invites:{code:string;created_at:number;expires_at:number;revoked_at:number|null;inviter_name:string}[];next_cursor:string|null}>('GET',withCursor('/api/admin/users/unassigned-invites',cursor,{limit:'20'})).then(page<'invites',{code:string;created_at:number;expires_at:number;revoked_at:number|null;inviter_name:string}>('invites')),
  savePilot:(max:number,invites:number,open:boolean)=>call<{pilot:PilotState}>('PUT','/api/admin/settings',{pilot_max_members:max,pilot_member_invite_limit:invites,registration_open:open}),
  predefine:(emails:string[],preview:boolean)=>call<PredefinePreview>('POST','/api/admin/users/predefine',{emails,preview}),
  userStatus:(users:{id:number;version:number}[],action:'activate'|'suspend'|'deactivate',reason:string)=>call<{results:{id:number;ok:boolean;code?:string}[]}>('POST','/api/admin/users/status',{users,action,reason}),
  userDetails:(id:number)=>call<{invitations:UserInvitation[];history:{action:string;detail:string;created_at:number;actor_name:string|null}[]}>('GET',`/api/admin/users/${id}/details`),
  adminSettings: () => call<{ registration_open: boolean; launch: LaunchState }>("GET", "/api/admin/settings"),
  setRegistrationOpen: (open: boolean) => call<{ registration_open: boolean; launch: LaunchState }>("PUT", "/api/admin/settings", { registration_open: open }),
  setLaunchOpen: (open: boolean) => call<{ registration_open: boolean; launch: LaunchState }>("PUT", "/api/admin/settings", { launch_open: open }),

  // Plans, stamps, orders (PLAN.md 4.24). Nothing is paid: an admin activates an order by hand.
  catalogue: () => call<Catalogue>("GET", "/api/billing/plans"),
  billing: () => call<BillingView>("GET", "/api/billing"),
  billingHistory: <K extends HistoryKind>(kind: K, cursor?: string) =>
    call<{ kind: K; items: (K extends "uses" ? UseRow : K extends "grants" ? GrantRow : OrderView)[]; next_cursor: string | null }>("GET", withCursor("/api/billing/history", cursor, { kind, limit: "20" })).then((r) => ({ items: r.items, next_cursor: r.next_cursor })),
  /** A member clicks a plan card: the provider's checkout to open inside Billing (PLAN.md 4.26 P3). Grants nothing by itself. */
  checkout: (priceId: string) => call<{ order: OrderView; checkout: { provider: string; environment: string; id: string; url: string | null } }>("POST", "/api/billing/checkout", { price_id: priceId }),
  /** The purchase while the provider's checkout is open: `completed` once its event arrived. */
  /** `id`: our order id or the provider's checkout id (Paddle `_ptxn`). `rights_active`: the purchase's rights exist (PLAN.md 4.27 R11). */
  checkoutStatus: (id: string) => call<{ order: OrderView; rights_active: boolean }>("GET", `/api/billing/checkout/${encodeURIComponent(id)}`),
  /** A fresh link to the provider's customer portal (invoices, payment method, cancelling). */
  portal: () => call<{ url: string }>("POST", "/api/billing/portal", {}).then((r) => r.url),
  adminMemberBilling: (memberId: number) => call<BillingView>("GET", `/api/admin/members/${memberId}/billing`),
  adminGrantStamps: (memberId: number, body: { units: number; reason: string; idempotency_key: string; expires_at?: number }) => call<{ grant: GrantRow }>("POST", `/api/admin/members/${memberId}/stamps`, body),
  adminGrantRight: (memberId: number, body: { plan_id: "free" | "legacy" | "start"; reason: string; idempotency_key: string; ends_at?: number }) => call<{ entitlement: EntitlementView }>("POST", `/api/admin/members/${memberId}/entitlements`, body),
  adminGrantStorage: (memberId: number, body: { bytes: number; reason: string; idempotency_key: string; ends_at?: number }) => call<{ entitlement: EntitlementView }>("POST", `/api/admin/members/${memberId}/storage-addons`, body),
  adminEndRight: (id: number, body: { reason: string; idempotency_key: string }) => call<{ result: "ended" | "already" }>("DELETE", `/api/admin/entitlements/${id}`, body),
  // The payment provider on Admin -> Payments (PLAN.md 4.26 step 3).
  adminPrices: () => call<AdminPrices>("GET", "/api/admin/billing/prices"),
  /** Owner only. */
  savePrice: (mapping: Omit<PriceMapping, "active"> & { active: boolean }) => call<{ price: PriceMapping }>("PUT", "/api/admin/billing/prices", mapping),
  waitingEvents: (cursor?: string) => call<{ events: WaitingEvent[]; next_cursor: string | null }>("GET", withCursor("/api/admin/billing/events", cursor, {})).then(page<"events", WaitingEvent>("events")),
  /** Owner only: applies again an event that granted nothing, once its price is mapped. */
  replayEvent: (provider: string, eventId: string) => call<{ status: string; detail?: string }>("POST", "/api/admin/billing/events/replay", { provider, event_id: eventId }),
  adminOrders: (status: "open" | "completed" | "all", cursor?: string) =>
    call<{ orders: AdminOrder[]; next_cursor: string | null }>("GET", withCursor("/api/admin/orders", cursor, { status, limit: "20" })).then(page<"orders", AdminOrder>("orders")),
  addressBook: () => call<StoredAddressBook>("GET", "/api/address-book"),
  /** `version`: the one read; 409 `address_book_changed` when another device saved since. */
  saveAddressBook: (version: number, blob: string) => call<{ version: number }>("PUT", "/api/address-book", { version, blob }),
  /** Needs a proof by the admin's own key over this member and reason (PLAN.md 4.13 R5). */
  resetIdentity: (memberId: number, reason: string, p: KeyProof) =>
    call<{ retired: string }>("POST", `/api/admin/members/${memberId}/reset-identity`, { reason, ...p }),
  /** Members for the admin page (PLAN.md 4.20): by name, searched by name or email; `total` counts every match. */
  adminMembers: (filters: { q: string; state: UserStatus | "all"; key?:string;referral?:string;kind?:string;role?:string }, cursor?: string) =>
    call<{ members: AdminMember[]; next_cursor: string | null; total: number }>("GET", withCursor("/api/admin/members", cursor, { limit: "50", state: filters.state, key:filters.key??"all",referral:filters.referral??"all",kind:filters.kind??"all",role:filters.role??"all", ...(filters.q ? { q: filters.q } : {}) })).then((r) => ({ ...page<"members", AdminMember>("members")(r), total: r.total })),
  /** The owner makes a member an admin or an admin a member again (PLAN.md 4.22), with the owner's key proof. */
  setRole: (memberId: number, role: "admin" | "member", reason: string, p: KeyProof) =>
    call<{ role: string }>("POST", `/api/admin/members/${memberId}/role`, { role, reason, ...p }),
  deactivate: (memberId: number) => call<{ ok: true }>("POST", `/api/admin/members/${memberId}/deactivate`),
  reactivate: (memberId: number) => call<{ ok: true }>("POST", `/api/admin/members/${memberId}/reactivate`),

  /** `blobs`: sha256 of the attachments the capsule uses, already uploaded. */
  submit: (event: Event, blobs: string[] = []) => call<Submitted>("POST", "/api/messages", { event, ...(blobs.length ? { blobs } : {}) }),
  /** A private capsule and the author's copy, stored whole or not at all (PLAN.md 4.11 H6). */
  submitPair: (wrap: Event, copy: Event, blobs: string[] = []) =>
    call<Submitted>("POST", "/api/messages", { events: [wrap, copy], ...(blobs.length ? { blobs } : {}) }),
  /** A capsule for an address without a member yet: my copy plus an invite and a mail (PLAN.md 4.14). */
  createPending: (body: { email: string; copy: Event; blobs: string[]; lang: string }) =>
    call<{ id: string; link: string; mailed: boolean; mail_error?: string }>("POST", "/api/pending", body),
  pendingIncoming: (cursor?: string) => call<{ pending: PendingIn[]; next_cursor: string | null }>("GET", withCursor("/api/pending/incoming", cursor, { limit: "20" })).then(page<"pending", PendingIn>("pending")),
  pendingOutgoing: (cursor?: string) => call<{ pending: PendingOut[]; next_cursor: string | null }>("GET", withCursor("/api/pending/outgoing", cursor, { limit: "20" })).then(page<"pending", PendingOut>("pending")),
  requestPending: (id: string, lang: string) => call<{ ok: true }>("POST", `/api/pending/${id}/request`, { lang }),
  /** `wrap`: my copy's capsule wrapped to the key that asked (rewrapPrivate). */
  approvePending: (id: string, wrap: Event) => call<{ ok: true }>("POST", `/api/pending/${id}/approve`, { wrap }),
  declinePending: (id: string) => call<{ ok: true }>("POST", `/api/pending/${id}/decline`),
  /** Requests to me with a reply envelope I have not opened, for the pointer in Inbox (PLAN.md 4.32); counts up to `limit`. */
  unopenedRequests: (limit: number) =>
    call<MessagePage>("GET", withCursor("/api/correspondence/received", undefined, { limit: String(limit), read: "0", blocked: "0" })).then((p) => p.messages.length),
  /** One stored message I may see (my own submissions, private wraps to me, public capsules). */
  message: (id: string) => call<MessageRow>("GET", `/api/messages/${id}`),
  /** One capsule to 2 to 20 followed members (PLAN.md 4.14), stored whole or not at all. */
  submitGroup: (wraps: Event[], copy: Event | null, blobs: string[] = []) =>
    call<Submitted & { rejected: string[] }>("POST", "/api/messages/group", { wraps, ...(copy ? { copy } : {}), ...(blobs.length ? { blobs } : {}) }),

  /** Uploads an encrypted attachment (ciphertext only) under its SHA-256. */
  async uploadBlob(sha256: string, ciphertext: Uint8Array): Promise<void> {
    const res = await fetch(`/api/blobs/${sha256}`, {
      method: "PUT",
      credentials: "same-origin",
      headers: { "content-type": "application/octet-stream" },
      body: ciphertext.slice().buffer as ArrayBuffer,
    });
    if (!res.ok) {
      const body = (await res.json().catch(() => null)) as { error?: string; code?: string } | null;
      throw new ApiError(res.status, body?.error ?? res.statusText, body?.code ?? null);
    }
  },
  /** Downloads an encrypted attachment; decryptFile checks it against the manifest. */
  async fetchBlob(sha256: string): Promise<Uint8Array> {
    const res = await fetch(`/api/blobs/${sha256}`, { credentials: "same-origin" });
    if (!res.ok) throw new ApiError(res.status, res.status === 404 ? msg().attachments.notFound : res.statusText);
    return new Uint8Array(await res.arrayBuffer());
  },
  /** `follows`: members I follow; `authors`: every key (also retired) of those members. */
  follows: () => call<{ follows: Follow[]; authors: string[] }>("GET", "/api/follows"),
  followers: (cursor?: string) => call<{ followers: PersonEntry[]; next_cursor: string | null }>("GET", withCursor("/api/followers", cursor, { limit: "50" })).then(page<"followers", PersonEntry>("followers")),
  blocks: () => call<{ blocks: PersonEntry[] }>("GET", "/api/blocks"),
  hides: () => call<{ hides: PersonEntry[] }>("GET", "/api/hides").then((r) => r.hides),
  setBlock: (target: number, on: boolean) => call(on ? "PUT" : "DELETE", `/api/blocks/${target}`),
  setHide: (target: number, on: boolean) => call(on ? "PUT" : "DELETE", `/api/hides/${target}`),
  /** A member id or any key of the member. */
  saveSettings: (settings: { hide_senders?: boolean; lang?: string }) => call<{ settings: { hide_senders: boolean } }>("PUT", "/api/settings", settings),
  /** Capsule notifications by mail (PLAN.md 4.28): what the member wants, with the defaults filled in. */
  notifications: () => call<NotificationsAnswer>("GET", "/api/notifications"),
  saveNotifications: (notifications: Partial<Record<NotificationKind, Partial<NotificationPrefs>>>, lang: string) => call<NotificationsAnswer>("PUT", "/api/notifications", { notifications, lang }),
  /** The link of a notification mail: no session needed, the token is the proof. */
  unsubscribe: (t: string) => call<{ ok: true; kind: NotificationKind }>("POST", "/api/notifications/unsubscribe", { t }),
  follow: (target: string | number) => call("PUT", `/api/follows/${target}`),
  unfollow: (target: string | number) => call("DELETE", `/api/follows/${target}`),
  watchIds: () => call<{ ids: string[] }>("GET", "/api/watches").then((r) => r.ids),
  archives: () => call<{ ids: string[] }>("GET", "/api/archives").then((r) => r.ids),
  archive: (id: string) => call("PUT", `/api/archives/${id}`),
  restore: (id: string) => call("DELETE", `/api/archives/${id}`),
  /** The capsule was opened: it is no longer new in this account's lists, on any device. */
  markRead: (id: string) => call("PUT", `/api/reads/${id}`),
  /** One page of a list as `listQuery` builds it (PLAN.md 4.20); `cursor` is the previous page's `next_cursor`. */
  list: <T extends MessageRow = MessageRow>(query: { path: string; params: Record<string, string> }, cursor?: string, signal?: AbortSignal) =>
    call<MessagePage<T>>("GET", `${query.path}?${new URLSearchParams({ ...query.params, ...(cursor ? { cursor } : {}) })}`, undefined, signal),
  watch: (id: string) => call("PUT", `/api/watches/${id}`),
  unwatch: (id: string) => call("DELETE", `/api/watches/${id}`),
};

/** Google "confirm it is you", in a popup that ends on /reauth-done (PLAN.md 4.16 K3). */
export function googleReauthUrl(): string {
  return "/api/auth/google/start?reauth=1";
}

/**
 * Where a sign-in on this page returns to: the page itself, so a link from a mail (a capsule, a
 * request to connect) opens after the sign-in. The sign-in's own parameters are left out.
 */
export function returnPath(): string {
  const params = new URLSearchParams(location.search);
  for (const key of ["invite", "recover", "lang"]) params.delete(key);
  const query = params.toString();
  return `${location.pathname}${query ? `?${query}` : ""}`;
}

/** Any page (a request to connect, a capsule from a mail) comes back to itself after a Google sign-in. */
export function googleLoginUrl(invite: string | null): string {
  const params = new URLSearchParams();
  if(new URLSearchParams(location.search).get('recover')==='1') params.set('recover','1');
  if (invite) params.set("invite", invite);
  const next = returnPath();
  if (next !== "/") params.set("next", next);
  const query = params.toString();
  return `/api/auth/google/start${query ? `?${query}` : ""}`;
}
